import { createAction, PayloadAction } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "../../../common/redux/createReducer";
import {
	InteropStore,
	WorkspaceStore,
	ClientAdded,
	ClientRemoved,
	OutgoingDispatchMessage,
	InteropBroadcastInternal,
} from "../types";
import resolver from "./resolver";
import { StoreType } from "../store";
import { ContextAny } from "../../../clients/Interop/types";
import { setComponentState, getComponentState, uuidsFromWindowName } from "./commonUtils";

const { workspaceUuid } = resolver;

const { clientAdded, clientRemoved } = resolver.actions;

// ------------- Types

type PersistedData = {
	persistedData?: Record<string, ContextAny>;
	windowName: string;
};

// ------- Helper functions

const getWorkspaceState = async (windowName: string): Promise<PersistedData> => {
	const data = await getComponentState({ field: "Interop-#Workspace-contexts", windowName });
	return {
		persistedData: data,
		windowName,
	};
};

const actions = {
	loadThenBroadcastData: createAction<PersistedData>("workspace/loadThenBroadcastData"),
	listener: createAction<OutgoingDispatchMessage>("workspace/listener"),
};

const initialize = (store: StoreType) => {
	store.dispatch(
		resolver.actions.registerSelectConnectChannel({
			name: workspaceUuid,
			dispatchAction: "workspace/listener",
		})
	);
};

const getInitialState = (): WorkspaceStore => {
	return {
		persistedData: {},
	};
};

const addReducerCases = (builder: ActionReducerMapBuilder<InteropStore>) => {
	builder
		/**
		 * Initialize the new client with any contexts that were fetched from the workspace.
		 * Contexts are delivered by dispatching the resolver's "broadcast" action.
		 * We set the `to` field in the action's "meta" section so that the context is directed to the specific client rather than to any subscriber.
		 */
		.addCase(actions.loadThenBroadcastData, (state, action: PayloadAction<PersistedData>, cmd) => {
			const { windowName, persistedData } = action.payload;
			if (persistedData) {
				state.workspace.persistedData[windowName] = persistedData;
				Object.values(persistedData).forEach((context) => {
					if (context.type) {
						uuidsFromWindowName({ clients: state.resolver.clients, windowName }).forEach((uuid) => {
							const interopBroadcast: InteropBroadcastInternal = {
								type: "broadcast",
								payload: {
									context,
								},
								meta: {
									uuid: workspaceUuid,
									noSelectConnect: true,
									to: uuid,
								},
							};
							cmd?.action(resolver.actions.broadcast(interopBroadcast));
						});
					}
				});
			}
		})
		/**
		 * When a new client is added, check to see if we have any contexts saved to deliver to the app
		 * Whatever comes back fro the workspace is dispatched to "initializeContext"
		 */
		.addCase(clientAdded, (state, action: PayloadAction<ClientAdded>, cmd) => {
			const { windowName } = action.payload;
			cmd?.run(getWorkspaceState, {
				args: [windowName],
				successActionCreator: actions.loadThenBroadcastData,
			});
		})
		.addCase(clientRemoved, (state, action: PayloadAction<ClientRemoved>, cmd) => {
			const { windowName } = action.payload;
			delete state.workspace.persistedData[windowName];
		})
		/**
		 * This is the end result of any #Workspace SelectConnect rule. The outgoing dispatch message should include a context
		 * which is saved to the workspace (alongside any other previously saved contexts).
		 */
		.addCase(actions.listener, (state, action: PayloadAction<OutgoingDispatchMessage>, cmd) => {
			const { context, uuid } = action.payload;
			const { windowName } = state.resolver.clients[uuid];
			// get current state, append/replace this new state, save to workspace
			const originalData = state.workspace.persistedData[windowName] ?? {};

			if (context) {
				const replacementData = {
					...originalData,
					[context.type]: context,
				};
				state.workspace.persistedData[windowName] = replacementData;
				cmd?.run(setComponentState, {
					args: [{ field: "Interop-#Workspace-contexts", value: replacementData, windowName }],
				});
			}
		});
};

export default {
	getInitialState,
	addReducerCases,
	actions,
	initialize,
};
